/* eslint-disable max-lines */
import { useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import StepShell from '../StepShell';
import SignatureBottomSheet from '../components/SignatureBottomSheet';
import InlinePdfViewer from '../components/InlinePdfViewer';
import { colors, layout } from '../styles';
import HireDocuments from '../../../../../services/HireDocuments';
import { pdfByType } from '../../../../../helpers/pdfByType';
import { buildEmployeeChildcareTuitionPdf } from '../utils/pdfBuilders';
import { useLivePdfPreview } from '../utils/livePdfPreview';
import { getPreEmploymentLookup } from '../utils/preEmploymentLookup';
import { BaseStepProps } from './BaseStepProps';

interface ChildcareTuitionDraft {
  employeeName: string;
  childrenEnrolled: string;
  preferredPaymentMethod: 'Credit Card' | 'Debit Card' | 'ACH' | '';
}

const empty: ChildcareTuitionDraft = {
  employeeName: '',
  childrenEnrolled: '',
  preferredPaymentMethod: '',
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const radioStyle = (selected: boolean): React.CSSProperties => ({
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: `2px solid ${selected ? colors.accent : '#9CA3AF'}`,
  position: 'relative',
  flexShrink: 0,
});

const radioInner: React.CSSProperties = {
  position: 'absolute',
  top: 2,
  left: 2,
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: colors.accent,
};

const PAYMENT_OPTIONS: { value: ChildcareTuitionDraft['preferredPaymentMethod']; label: string }[] = [
  { value: 'Credit Card', label: 'Credit Card' },
  { value: 'Debit Card', label: 'Debit Card' },
  { value: 'ACH', label: 'ACH Bank Transfer' },
];

const EmployeeChildcareTuitionStep = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  application,
  applicationDraft,
  draftDocs,
  draft,
  saveDraft,
  clearDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
}: BaseStepProps) => {
  const config = pdfByType.find((p) => p.type === 'EMPLOYEE_CHILDCARE_TUITION');
  const pdfUrl = step.pdfUrl || config?.url;
  const backendDraft = draftDocs.find((d) => d.type === 'EMPLOYEE_CHILDCARE_TUITION');
  const lookup = getPreEmploymentLookup(application, applicationDraft, token);

  const initialFromBackend: ChildcareTuitionDraft = {
    ...empty,
    employeeName: lookup.fullName,
    ...(backendDraft?.options?.fields || {}),
  };
  const initial = (draft as ChildcareTuitionDraft) || initialFromBackend;
  const [data, setData] = useState<ChildcareTuitionDraft>({ ...empty, ...initial });
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  const update = (patch: Partial<ChildcareTuitionDraft>) => {
    const next = { ...data, ...patch };
    setData(next);
    saveDraft(next);
  };

  const canOpenSignature =
    !!data.employeeName && !!data.childrenEnrolled && !!data.preferredPaymentMethod && !savingDraft;

  const livePdfUrl = useLivePdfPreview({
    builder: async () =>
      pdfUrl && config
        ? buildEmployeeChildcareTuitionPdf(pdfUrl, config, {
            employeeName: data.employeeName,
            childrenEnrolled: data.childrenEnrolled,
            preferredPaymentMethod: data.preferredPaymentMethod,
            signatureDate: '',
          })
        : undefined,
    deps: [data, pdfUrl, config],
    enabled: !!pdfUrl && !!config,
  });

  const submitWithSignature = async (signaturePng: string) => {
    if (!config || !pdfUrl) return;
    const pdfBytes = await buildEmployeeChildcareTuitionPdf(
      pdfUrl,
      config,
      {
        employeeName: data.employeeName,
        childrenEnrolled: data.childrenEnrolled,
        preferredPaymentMethod: data.preferredPaymentMethod,
        signatureDate: moment().format('YYYY-MM-DD'),
      },
      signaturePng,
    );
    const pdfSigned = await blobToBase64(new Blob([pdfBytes as any], { type: 'application/pdf' }));

    await HireDocuments('').create({
      type: 'EMPLOYEE_CHILDCARE_TUITION',
      userId: tokenData.userId,
      userName: tokenData.userName,
      userToken: token,
      pdfUrl,
      pdfSigned,
      signature: signaturePng,
      isDraft: false,
      options: {
        ...config,
        fields: {
          employeeName: data.employeeName,
          childrenEnrolled: data.childrenEnrolled,
          preferredPaymentMethod: data.preferredPaymentMethod,
        },
        signPng: signaturePng,
        signatureDate: moment().format('YYYY-MM-DD'),
      },
    });

    clearDraft();
    toast.success('Childcare Tuition Agreement signed.');
    setSignatureOpen(false);
    onNext();
  };

  const handleSaveLater = async () => {
    if (!config || !pdfUrl || savingDraft) return;
    setSavingDraft(true);
    try {
      await HireDocuments('').create({
        type: 'EMPLOYEE_CHILDCARE_TUITION',
        userId: tokenData.userId,
        userName: tokenData.userName,
        userToken: token,
        pdfUrl,
        pdfSigned: null,
        signature: '',
        isDraft: true,
        options: {
          ...config,
          fields: {
            employeeName: data.employeeName,
            childrenEnrolled: data.childrenEnrolled,
            preferredPaymentMethod: data.preferredPaymentMethod,
          },
          signPng: '',
        },
      });
      clearDraft();
      toast.success('Progress saved.');
      onSaveLater();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not save progress.');
    } finally {
      setSavingDraft(false);
    }
  };

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={handleSaveLater}
      onNext={() => setSignatureOpen(true)}
      nextLabel="Accept & Sign"
      nextDisabled={!canOpenSignature}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Employee Childcare Tuition Agreement</h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6 }}>
        Scroll through the agreement below, fill the fields, then sign at the bottom.
      </p>

      {pdfUrl && <InlinePdfViewer url={livePdfUrl || pdfUrl} maxHeight="55vh" />}

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Employee Name *</label>
        <input
          type="text"
          value={data.employeeName}
          onChange={(e) => update({ employeeName: e.target.value })}
          style={layout.input}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Child(ren) Enrolled at AEA *</label>
        <input
          type="text"
          value={data.childrenEnrolled}
          onChange={(e) => update({ childrenEnrolled: e.target.value })}
          placeholder="Full name(s), separated by commas"
          style={layout.input}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={layout.label}>Preferred Payment Method *</label>
        {PAYMENT_OPTIONS.map((opt) => (
          <div
            key={opt.value}
            onClick={() => update({ preferredPaymentMethod: opt.value })}
            style={{
              ...layout.optionCard,
              ...(data.preferredPaymentMethod === opt.value ? layout.optionCardSelected : {}),
            }}
          >
            <div style={radioStyle(data.preferredPaymentMethod === opt.value)}>
              {data.preferredPaymentMethod === opt.value && <div style={radioInner} />}
            </div>
            {opt.label}
          </div>
        ))}
      </div>

      <SignatureBottomSheet
        isOpen={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        onSubmit={submitWithSignature}
        title="Sign Childcare Tuition Agreement"
        submitLabel="Accept & Sign"
        previewPdfUrl={livePdfUrl}
      />
    </StepShell>
  );
};

export default EmployeeChildcareTuitionStep;
